import './settings-leaf.css';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../state/session.tsx';
import { Checkbox, Select, TextArea } from '../ui.tsx';
import { MoneyInput } from '../money-input.tsx';
import { NumberInput } from '../number-input.tsx';
import {
  BILLING_INCREMENTS,
  PLANS,
  SERIES_FREQUENCIES,
  minimumPlanFor,
  type SeriesFrequency,
} from '@shared/constants/index.ts';
import type { FrequencyTierDefault } from '@shared/frequency-tiers.ts';
import { usePlanFeature } from '../plan-gate.tsx';
import { useAppConfig } from '../turnstile.tsx';
import { CardSaveFooter, useCompanyCardForm } from './company-card-form.tsx';
import type { Company } from '@shared/types/index.ts';

/**
 * What a new quote starts from: the rate, rounding, tax posture and terms.
 * None of it reaches back into quotes that already exist. Quote and invoice
 * numbering is its own card (`numbering-card.tsx`).
 */

const snapshot = (company: Company) => ({
  defaultHourlyRateCents: company.defaultHourlyRateCents,
  billingIncrementMinutes: company.billingIncrementMinutes,
  defaultQuoteExpirationDays: company.defaultQuoteExpirationDays,
  taxEnabled: company.taxEnabled,
  defaultTaxRateBasisPoints: company.defaultTaxRateBasisPoints,
  laborIsTaxable: company.laborIsTaxable,
  showTimeOnQuote: company.showTimeOnQuote,
  showHourlyRateOnQuote: company.showHourlyRateOnQuote,
  quoteBrandingEnabled: company.quoteBrandingEnabled,
  requireQuoteSignature: company.requireQuoteSignature,
  quoteInstantBookingEnabled: company.quoteInstantBookingEnabled,
  defaultTerms: company.defaultTerms,
  frequencyTierSettings: company.frequencyTierSettings,
});

/**
 * `quoteBrandingEnabled` is deliberately absent when the plan cannot change
 * it: the card reads the EFFECTIVE state (a Free company sees the credit, so
 * the box is checked), and sending that back would overwrite the preference a
 * paid company had stored before it fell to Free — the one thing subscribing
 * again is supposed to restore.
 */
const toPayload = (draft: ReturnType<typeof snapshot>, canRemoveBranding: boolean) => ({
  defaultHourlyRateCents: draft.defaultHourlyRateCents,
  billingIncrementMinutes: draft.billingIncrementMinutes,
  defaultQuoteExpirationDays: draft.defaultQuoteExpirationDays,
  taxEnabled: draft.taxEnabled,
  defaultTaxRateBasisPoints: draft.defaultTaxRateBasisPoints,
  laborIsTaxable: draft.laborIsTaxable,
  showTimeOnQuote: draft.showTimeOnQuote,
  showHourlyRateOnQuote: draft.showHourlyRateOnQuote,
  ...(canRemoveBranding ? { quoteBrandingEnabled: draft.quoteBrandingEnabled } : {}),
  requireQuoteSignature: draft.requireQuoteSignature,
  quoteInstantBookingEnabled: draft.quoteInstantBookingEnabled,
  defaultTerms: draft.defaultTerms ?? '',
  frequencyTierSettings: draft.frequencyTierSettings,
});

export default function PricingCard() {
  const { t } = useTranslation();
  // The currency is the company card's decision; this card only formats with
  // it, and reads the canonical value so a saved change shows up here at once.
  const { company } = useSession();
  // Turning the credit OFF is the plan feature; carrying it is not.
  const canRemoveBranding = usePlanFeature('unbranded_quotes');
  const form = useCompanyCardForm(snapshot, (draft) => toPayload(draft, canRemoveBranding));
  const appConfig = useAppConfig().data;

  const draft = form.draft;
  if (!draft || !company) return null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="pricing-heading">
      <div className="work-section-heading">
        <h2 id="pricing-heading" className="mb-4 text-lg font-semibold">
          {t('settings.pricingHeading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.pricingDescription')}</p>

      <div className="grid gap-x-4 sm:grid-cols-2">
        <MoneyInput
          currencyCode={company.currencyCode}
          label={t('settings.hourlyRate')}
          valueCents={draft.defaultHourlyRateCents}
          disabled={form.readOnly}
          min={0}
          error={form.fieldErrors.defaultHourlyRateCents}
          onChangeCents={(cents) => form.set('defaultHourlyRateCents', cents ?? 0)}
        />
        <Select
          label={t('settings.roundUpTo')}
          value={String(draft.billingIncrementMinutes)}
          disabled={form.readOnly}
          onChange={(event) => form.set('billingIncrementMinutes', Number(event.target.value))}
          options={BILLING_INCREMENTS.map((increment) => ({
            value: String(increment.minutes),
            label: t(`schedule.billingIncrement.${increment.minutes}`),
          }))}
        />
        <NumberInput
          label={t('settings.expirationDays')}
          min={1}
          max={365}
          value={draft.defaultQuoteExpirationDays}
          disabled={form.readOnly}
          error={form.fieldErrors.defaultQuoteExpirationDays}
          onValueChange={(days) => form.set('defaultQuoteExpirationDays', days ?? 30)}
        />
      </div>

      <Checkbox
        label={t('settings.chargeTax')}
        checked={draft.taxEnabled}
        disabled={form.readOnly}
        onChange={(event) => form.set('taxEnabled', event.target.checked)}
      />
      {draft.taxEnabled ? (
        <div className="grid gap-x-4 sm:grid-cols-2">
          <NumberInput
            label={t('settings.taxRate')}
            min={0}
            max={100}
            step={0.001}
            // Stored as basis points; shown as a percentage. Zero is "we do
            // not charge tax", so it shows as an empty box rather than a 0
            // that has to be selected before a rate can be typed.
            blankValue={0}
            value={draft.defaultTaxRateBasisPoints / 100}
            disabled={form.readOnly}
            onValueChange={(percent) =>
              form.set('defaultTaxRateBasisPoints', Math.max(0, Math.round((percent ?? 0) * 100)))
            }
          />
          <div className="pt-7">
            <Checkbox
              label={t('settings.laborTaxable')}
              hint={t('settings.laborTaxableHint')}
              checked={draft.laborIsTaxable}
              disabled={form.readOnly}
              onChange={(event) => form.set('laborIsTaxable', event.target.checked)}
            />
          </div>
        </div>
      ) : null}

      <div className="grid gap-x-4 sm:grid-cols-2">
        <Checkbox
          label={t('settings.showTime')}
          checked={draft.showTimeOnQuote}
          disabled={form.readOnly}
          onChange={(event) => form.set('showTimeOnQuote', event.target.checked)}
        />
        <Checkbox
          label={t('settings.showRate')}
          checked={draft.showHourlyRateOnQuote}
          disabled={form.readOnly}
          onChange={(event) => form.set('showHourlyRateOnQuote', event.target.checked)}
        />
      </div>

      {/* The credit every quote carries. Locked rather than hidden on a plan
          that may not remove it: a setting somebody cannot find is a setting
          they will ask support about, and the point of the lock is that it
          names the plan that opens it. */}
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Checkbox
          label={t('settings.quoteBranding', { brand: appConfig?.brandLabel ?? t('settings.quoteBrandingFallback') })}
          checked={draft.quoteBrandingEnabled}
          disabled={form.readOnly || !canRemoveBranding}
          onChange={(event) => form.set('quoteBrandingEnabled', event.target.checked)}
        />
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">
        {canRemoveBranding
          ? t('settings.quoteBrandingHint')
          : t('settings.quoteBrandingLocked', { plan: PLANS[minimumPlanFor('unbranded_quotes')].name })}
      </p>

      {/* Sits with the other "what the customer sees on a quote" switches,
          because that is where somebody looks for it — and the hint says what
          it costs, since a requirement the customer cannot meet is a quote
          that never gets accepted. */}
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Checkbox
          label={t('settings.requireQuoteSignature')}
          checked={draft.requireQuoteSignature}
          disabled={form.readOnly}
          onChange={(event) => form.set('requireQuoteSignature', event.target.checked)}
        />
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.requireQuoteSignatureHint')}</p>
      <Checkbox
        label={t('settings.quoteInstantBookingEnabled')}
        checked={draft.quoteInstantBookingEnabled}
        disabled={form.readOnly}
        onChange={(event) => form.set('quoteInstantBookingEnabled', event.target.checked)}
      />
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('settings.quoteInstantBookingHint')}</p>

      {/* Frequency pricing tiers (migration 0110): which frequencies a new
          residential quote offers, and the standing discount for each. Per
          quote the estimator can still adjust or switch the offer off. */}
      <div className="mb-4 border-t border-[color:var(--color-line)] pt-4">
        <h3 className="mb-1 font-semibold">{t('settings.frequencyTiersHeading')}</h3>
        <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('settings.frequencyTiersDescription')}</p>
        <Checkbox
          label={t('settings.frequencyTiersOfferOnNew')}
          hint={t('settings.frequencyTiersOfferOnNewHint')}
          checked={draft.frequencyTierSettings.offerOnNewQuotes}
          disabled={form.readOnly}
          onChange={(event) =>
            form.set('frequencyTierSettings', {
              ...draft.frequencyTierSettings,
              offerOnNewQuotes: event.target.checked,
            })
          }
        />
        <div className="grid gap-x-4 sm:grid-cols-2">
          {SERIES_FREQUENCIES.map((frequency) => {
            const tier = draft.frequencyTierSettings.tiers[frequency];
            const setTier = (patch: Partial<FrequencyTierDefault>) =>
              form.set('frequencyTierSettings', {
                ...draft.frequencyTierSettings,
                tiers: {
                  ...draft.frequencyTierSettings.tiers,
                  [frequency]: { ...tier, ...patch },
                },
              });
            return (
              <div key={frequency} className="flex items-start gap-3">
                <div className="grow pt-7">
                  <Checkbox
                    label={t(`schedule.frequency.${frequency}`)}
                    checked={tier.offered}
                    disabled={form.readOnly}
                    onChange={(event) => setTier({ offered: event.target.checked })}
                  />
                </div>
                <div className="w-28">
                  <NumberInput
                    label={t('settings.frequencyTierPercentOff')}
                    min={0}
                    max={100}
                    step={0.5}
                    blankValue={0}
                    value={tier.discountBasisPoints / 100}
                    disabled={form.readOnly || !tier.offered}
                    onValueChange={(percent: number | null) =>
                      setTier({
                        discountBasisPoints: Math.max(0, Math.min(10_000, Math.round((percent ?? 0) * 100))),
                      })
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TextArea
        label={t('settings.defaultTerms')}
        rows={6}
        hint={t('settings.defaultTermsHint')}
        value={draft.defaultTerms ?? ''}
        disabled={form.readOnly}
        onChange={(event) => form.set('defaultTerms', event.target.value)}
      />

      <CardSaveFooter form={form} saveLabel={t('settings.savePricing')} />
    </section>
  );
}
